import { useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import {
  conditions,
  priorities,
  sampleStatuses,
  sampleTypes,
} from '../../constants/statusOptions';
import type { GridColumn } from '../../components/tables/EditableGrid';

interface BulkUpdateBarProps {
  selectedCount: number;
  columns: GridColumn[];
  onApply: (field: string, value: string) => void;
}

/** Fields that support bulk update — must match backend whitelist */
const bulkUpdatableFields = new Set([
  'Status',
  'Priority',
  'Condition',
  'BoxOrBucketNumber',
  'ShipmentNumber',
  'SampleComments',
  'StorageLocation',
  'SampleType',
]);

const optionsMap: Record<string, readonly string[]> = {
  Status: sampleStatuses,
  Priority: priorities,
  Condition: conditions,
  SampleType: sampleTypes,
};

export const BulkUpdateBar = ({ selectedCount, columns, onApply }: BulkUpdateBarProps) => {
  const theme = useTheme();
  const [selectedField, setSelectedField] = useState('');
  const [value, setValue] = useState('');

  const updatableColumns = columns.filter((c) => bulkUpdatableFields.has(c.field));
  const activeColumn = updatableColumns.find((c) => c.field === selectedField);
  const fieldOptions = selectedField ? optionsMap[selectedField] : undefined;

  const handleApply = () => {
    if (!selectedField || !value.trim()) return;
    onApply(selectedField, value.trim());
    setValue('');
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: 2,
        py: 0.75,
        backgroundColor: alpha(theme.palette.background.paper, 0.95),
        backdropFilter: 'blur(8px)',
        border: `1px solid ${theme.palette.divider}`,
        borderTop: 'none',
        position: 'fixed',
        top: 60,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 1200,
        boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
        borderRadius: '0 0 12px 12px',
        animation: 'bulkBarSlideDown 0.25s ease-out',
        '@keyframes bulkBarSlideDown': {
          '0%': { transform: 'translateX(-50%) translateY(-100%)', opacity: 0 },
          '100%': { transform: 'translateX(-50%) translateY(0)', opacity: 1 },
        },
      }}
    >
      <Typography variant="body2" fontWeight={600} sx={{ whiteSpace: 'nowrap' }}>
        Bulk Update ({selectedCount} selected):
      </Typography>

      <Autocomplete
        size="small"
        options={updatableColumns}
        getOptionLabel={(opt) => opt.headerName}
        value={activeColumn ?? null}
        onChange={(_e, val) => {
          setSelectedField(val?.field ?? '');
          setValue('');
        }}
        renderInput={(params) => (
          <TextField {...params} placeholder="Select column..." />
        )}
        sx={{ width: 200 }}
        disableClearable={false}
      />

      {selectedField && (
        fieldOptions ? (
          <TextField
            size="small"
            select
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="Select value..."
            sx={{ width: 180 }}
          >
            {fieldOptions.map((opt) => (
              <MenuItem key={opt} value={opt}>{opt}</MenuItem>
            ))}
          </TextField>
        ) : (
          <TextField
            size="small"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={`Enter ${activeColumn?.headerName ?? 'value'}...`}
            sx={{ width: 180 }}
            onKeyDown={(e) => { if (e.key === 'Enter') handleApply(); }}
          />
        )
      )}

      <Button
        variant="contained"
        size="small"
        disabled={!selectedField || !value.trim()}
        onClick={handleApply}
        sx={{ textTransform: 'none', fontWeight: 600, minWidth: 80 }}
      >
        Apply
      </Button>
    </Box>
  );
};
